import { inject, Signal } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, type, withMethods } from '@ngrx/signals';
import {
  EntityProps,
  EntityState,
  removeEntity,
  setAllEntities,
  updateEntity,
  upsertEntity,
} from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap, tap } from 'rxjs';
import { SelectedEntityState } from '../../../shared/store/selected-entity.feature';
import { TechniqueAnimation } from '../model/technique-animation.model';
import { TechniqueAnimationService } from '../technique-animation.service';
import { CreateRequestTechniqueAnimation } from '../request/create-request';
import { toast } from 'ngx-sonner';
import { ToastrErrorComponent } from '@shared/components/toastr/toastr-error.component';
import { v7 as uuidv7 } from 'uuid';

export function avecMéthodesTechniquesAnimation<_>() {
  return signalStoreFeature(
    {
      state: type<EntityState<TechniqueAnimation> & SelectedEntityState>(),
      props: type<EntityProps<TechniqueAnimation> & { selectedEntity: Signal<TechniqueAnimation | null> }>(),
    },
    withMethods((store) => ({
      chargerTechniquesAnimation: (state: { techniquesAnimation: TechniqueAnimation[] }) =>
        patchState(store, setAllEntities(state.techniquesAnimation)),
      ajouterTechniqueAnimation: (state: { techniqueAnimation: TechniqueAnimation }) =>
        patchState(store, upsertEntity(state.techniqueAnimation)),
      modifierTechniqueAnimation: (state: { techniqueAnimation: TechniqueAnimation }) =>
        patchState(store, updateEntity({ id: state.techniqueAnimation.id, changes: state.techniqueAnimation })),
      supprimerTechniqueAnimation: (id: string) => patchState(store, removeEntity(id)),
    })),
    withMethods((store, service = inject(TechniqueAnimationService)) => ({
      modifier: rxMethod<{ techniqueAnimation: TechniqueAnimation; next: () => void }>(
        pipe(
          tap(({ techniqueAnimation }) => store.modifierTechniqueAnimation({ techniqueAnimation })),
          switchMap(({ techniqueAnimation, next }) =>
            service.modifier(techniqueAnimation).pipe(
              tapResponse({
                next: () => next(),
                error: (err) => {
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la modification de la technique d'animation, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                  console.error(err);
                },
              }),
            ),
          ),
        ),
      ),
      ajouter: rxMethod<{
        data: CreateRequestTechniqueAnimation;
        next?: () => void;
      }>(
        pipe(
          switchMap(({ data, next }) =>
            service.créer(data).pipe(
              tapResponse({
                next: () => {
                  next?.();
                },
                error: (err) => {
                  console.error("Error lors de l'ajout d'une technique d'animation via le Store: ", err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de l'ajout de la technique d'animation, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
      charger: rxMethod<void>(
        pipe(
          switchMap(() =>
            service.trouverTous().pipe(
              tapResponse({
                next: (response) => {
                  store.chargerTechniquesAnimation({ techniquesAnimation: response.items });
                },
                error: (err) => {
                  console.error("Error lors du chargement des techniques d'animation via le Store: ", err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors du chargement des techniques d'animation, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
      trouverParId: rxMethod<{
        id: string;
      }>(
        pipe(
          switchMap(({ id }) =>
            service.trouverParId(id).pipe(
              tapResponse({
                next: (techniqueAnimation) => {
                  store.ajouterTechniqueAnimation({ techniqueAnimation });
                },
                error: (err) => {
                  console.error("Error lors de l'ajout d'une technique d'animation via le Store: ", err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la récupération de la technique d'animation, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
      supprimer: rxMethod<string>(
        pipe(
          switchMap((id) =>
            service.supprimer(id).pipe(
              tapResponse({
                next: () => store.supprimerTechniqueAnimation(id),
                error: (err) => {
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la suppression de la technique d'animation, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                  console.error(err);
                },
              }),
            ),
          ),
        ),
      ),
    })),
  );
}
